// TODO (Core): Add unit tests for these functions
import {parseUrl} from '../../_web/utils/url'
import {getIsProductionAppEnv} from '../../utils/environment'
import {resolveCapabilities} from '../capabilities/resolveCapabilities'
import {brands} from '../config'
import {Brand, BrandCode, SubBrand, SubBrandKey} from '../types'

import type {SearchCapabilityContext} from '../capabilities/types/domains'

/**
 * Generic function to convert locale string to enum value using dynamic mapping.
 * @param localeString - The locale string to convert
 * @param localeMap - The mapping from enum values to locale strings
 * @returns The enum value for the locale string, or undefined if not found
 */
export const convertLocaleStringToEnum = <T extends string>(
  localeString: string,
  localeMap: Record<T, string>
): T | undefined => {
  // Find the enum key that maps to this locale string
  for (const [enumKey, localeStr] of Object.entries(localeMap)) {
    if (localeStr === localeString) {
      return enumKey as T
    }
  }
  return undefined
}

/**
 * Checks whether the passed-in brand is Vio
 * @param brand - The brand object to check
 * @returns True if brand.code is 'vio', otherwise false
 */
export const checkIsBrandVio = (brand: Brand) => brand.code === 'vio'

/**
 * Checks a language or currency code against the brand's allow-list
 * (`Brand.languageCodes` / `Brand.currencyCodes`). No list allows every code.
 */
export const isCodeAllowed = (code: string, allowedCodes?: string[]) =>
  !allowedCodes || allowedCodes.includes(code)

export const filterAllowedCodes = <T extends {code: string}>(
  items: T[],
  allowedCodes?: string[]
) => items.filter(({code}) => isCodeAllowed(code, allowedCodes))

/**
 * Resolves whether the brand serves the SEO-friendly SRP flow (Vio only today).
 * Reads the `search.hasSeoSrpFlow` capability via `resolveCapabilities` with
 * `skipClientOnly: true` because both call sites run outside the React tree
 * (`getServerSideProps` and the `getSrpSearchFlow` util), so
 * `useBrandCapabilities()` is unavailable. `skipClientOnly` keeps sibling
 * ClientOnly resolvers in other domains from firing with an empty context.
 * Must remain a static boolean — see sc-186150.
 * @param brand - The brand object to check
 * @returns True if the brand serves the SEO SRP flow, otherwise false
 */
export const getBrandHasSeoSrpFlow = (brand: Brand) =>
  resolveCapabilities(brand.capabilities, {} as SearchCapabilityContext, {
    skipClientOnly: true
  }).search.hasSeoSrpFlow

/**
 * Checks whether the brand is internal
 * @param brand - The brand object to check
 * @returns True if brand.isInternal is set to true, otherwise false
 */
export const checkIsBrandInternal = (brand: Brand) => brand.isInternal

/**
 * The affiliate a landing belongs to, as the `System_Attribution_Changed` event
 * reports it: `Vio` for Vio's own brands, the affiliate id on a white label.
 * @param brand - The brand object to resolve
 */
export const getAttributionBrand = (brand: Brand) =>
  brand.isInternal ? 'Vio' : brand.code

/**
 * Resolves a `partnerKey` to its owning parent brand and (optionally) sub-brand.
 *
 * PAPI treats every affiliate key as a separate affiliate, so sub-brands use their
 * own dedicated key (`SubBrand.partnerKey`) instead of sharing the parent
 * brand's `partnerKeys` list. A sub-brand match resolves to the **parent**
 * brand code — themes, site addresses and RUM tags all live there — and also
 * returns the matching `SubBrandKey` so callers can apply sub-brand defaults
 * (currency/language) and propagate `?subBrand=` on outbound URLs.
 */
const resolvePartnerKey = (
  partnerKey?: string
): {brandCode: BrandCode; subBrandKey?: SubBrandKey} | undefined => {
  if (!partnerKey) return undefined

  for (const brandCode in brands) {
    const typedBrandCode = brandCode as BrandCode
    const brand = brands[typedBrandCode]

    if (brand.partnerKeys?.includes(partnerKey)) {
      return {brandCode: brand.code}
    }

    const subBrands = brand.subBrand?.brands
    if (!subBrands) continue

    for (const subBrandKey in subBrands) {
      if (subBrands[subBrandKey]?.partnerKey === partnerKey) {
        return {brandCode: brand.code, subBrandKey}
      }
    }
  }

  return undefined
}

/**
 * Returns the brandCode for the matching partnerKey or "vio" if no matching partnerKey is found.
 * SubBrand keys resolve to the parent brand code — use `getSubBrandKeyFromPartnerKey`
 * alongside this when sub-brand context is needed.
 * @param partnerKey - The partnerKey to match against known brand and sub-brand partnerKeys
 * @returns The brand code matching the partnerKey, or 'vio' if none found
 */
export const getBrandCodeFromPartnerKey = (partnerKey?: string): BrandCode =>
  resolvePartnerKey(partnerKey)?.brandCode ?? brands.vio.code

/**
 * Returns the SubBrandKey when the partnerKey is assigned to a specific sub-brand,
 * or `undefined` when the key belongs to a parent brand (or is unknown).
 */
export const getSubBrandKeyFromPartnerKey = (
  partnerKey?: string
): SubBrandKey | undefined => resolvePartnerKey(partnerKey)?.subBrandKey

/**
 * Returns the Vio brand object
 * @returns The brand configuration for 'vio'
 */
export const getVioBrand = () => brands.vio

/**
 * Returns the search app host based on brand and app environment
 *  ie: https://www.goseek.com for goseek brand prod, https://tst.vio.com for vio brand staging
 * @param params - The parameters object
 * @param params.brand - The brand object containing site addresses
 * @param params.appEnv - The app environment string (e.g. "production", "staging", "e2e")
 * @returns The appropriate search app host URL
 */
export const getSearchHost = ({
  brand,
  appEnv
}: {
  brand: Brand
  appEnv?: string
}) => {
  const prodAddress =
    brand?.siteAddresses?.address || brands.vio?.siteAddresses?.address
  const stagingAddress =
    brand?.environmentAddresses?.stagingAddress ||
    brands.vio.environmentAddresses?.stagingAddress
  const e2eAddress =
    brand?.environmentAddresses?.e2eAddress ||
    brands.vio.environmentAddresses?.e2eAddress

  if (getIsProductionAppEnv(appEnv)) return prodAddress

  return (appEnv === 'e2e' && e2eAddress) || stagingAddress
}

/**
 * Returns the checkout app host based on brand and app environment
 * ie: https://securebooking.urlaubspiraten.de for holidaypiratesde prod, https://checkout.stag.bofh.fih.io for vio brand staging
 * @param params - The parameters object
 * @param params.brand - The brand object containing checkout addresses
 * @param params.appEnv - The app environment string (e.g. "production", "staging", "e2e")
 * @returns The appropriate checkout host URL
 */
export const getCheckoutHost = ({
  brand,
  appEnv
}: {
  brand: Brand
  appEnv?: string
}) => {
  const prodAddress =
    brand?.siteAddresses?.checkoutAddress ||
    brands.vio.siteAddresses?.checkoutAddress
  const stagingAddress =
    brand?.environmentAddresses?.stagingCheckoutAddress ||
    brands.vio.environmentAddresses?.stagingCheckoutAddress
  const e2eAddress =
    brand?.environmentAddresses?.e2eCheckoutAddress ||
    brands.vio.environmentAddresses?.e2eCheckoutAddress

  if (getIsProductionAppEnv(appEnv)) return prodAddress

  return (appEnv === 'e2e' && e2eAddress) || stagingAddress
}

const applyBookingsSearchParams = (
  url: URL,
  {
    anonymousId,
    userCountry,
    locale,
    currency
  }: {
    anonymousId?: string
    userCountry?: string
    locale?: string
    currency?: string
  }
) => {
  if (anonymousId) url.searchParams.set('anonymousId', anonymousId)
  if (userCountry) url.searchParams.set('userCountry', userCountry)
  if (locale) {
    url.searchParams.set('locale', locale)
    url.searchParams.set('lang', locale)
    url.searchParams.set('forceLanguageChange', '1')
  }
  if (currency) {
    url.searchParams.set('currency', currency)
    url.searchParams.set('forceCurrencyChange', '1')
  }
}

/**
 * Returns the bookings listing URL for the given brand.
 * ie: https://www.vio.com/bookings?anonymousId=...&locale=en&lang=en&forceLanguageChange=1 for vio prod
 * @param params - The parameters object
 * @param params.brand - The brand object used to resolve the search host
 * @param params.appEnv - The app environment string (e.g. "production", "staging")
 * @param params.anonymousId - Optional anonymous id to include as a query parameter
 * @param params.userCountry - Optional user country code to include as a query parameter
 * @param params.locale - Optional locale; when provided, also sets `lang` and `forceLanguageChange=1`
 * @param params.currency - Optional currency code; when provided, also sets `forceCurrencyChange=1`
 * @returns The bookings listing URL, or '' when the host cannot be resolved
 */
export const getMyBookingsUrl = ({
  brand,
  appEnv,
  anonymousId,
  userCountry,
  locale,
  currency
}: {
  brand: Brand
  appEnv: string
  anonymousId?: string
  userCountry?: string
  locale?: string
  currency?: string
}): string => {
  try {
    const host = getSearchHost({brand, appEnv})
    if (!host) return ''
    const url = new URL('/bookings', host)
    applyBookingsSearchParams(url, {anonymousId, userCountry, locale, currency})
    return url.toString()
  } catch {
    return ''
  }
}

/**
 * Normalizes a booking-management URL returned by the API by ensuring `lang`
 * and `currency` are present and setting `forceLanguageChange=1` /
 * `forceCurrencyChange=1`. This helps booking-management keep the caller's
 * language and currency instead of relying on cookies or brand defaults (which
 * might not exist for non-book-on-brand partners). Existing `lang` / `currency`
 * values on the URL are preserved.
 * @param url - The booking-management URL to normalize
 * @param params - The locale/currency overrides
 * @param params.locale - Optional locale; added as `lang` only when `lang` is not already present
 * @param params.currency - Optional currency code; added only when `currency` is not already present
 * @returns The URL with force flags applied, or the original URL when it's empty or malformed
 */
export const applyForceLangAndCurrencyFlags = (
  url: string,
  {locale, currency}: {locale?: string; currency?: string}
): string => {
  if (!url) return url
  try {
    const target = new URL(url)
    if (locale && !target.searchParams.has('lang')) {
      target.searchParams.set('lang', locale)
    }
    if (currency && !target.searchParams.has('currency')) {
      target.searchParams.set('currency', currency)
    }
    if (target.searchParams.has('lang')) {
      target.searchParams.set('forceLanguageChange', '1')
    }
    if (target.searchParams.has('currency')) {
      target.searchParams.set('forceCurrencyChange', '1')
    }
    return target.toString()
  } catch {
    return url
  }
}

/**
 * Returns the per-booking management URL for the given brand.
 * ie: https://www.vio.com/bookings/{bookingId}?anonymousId=...&locale=en&lang=en&forceLanguageChange=1 for vio prod
 * @param params - The parameters object
 * @param params.brand - The brand object used to resolve the search host
 * @param params.appEnv - The app environment string (e.g. "production", "staging")
 * @param params.bookingId - The booking id appended to the path
 * @param params.anonymousId - Optional anonymous id to include as a query parameter
 * @param params.userCountry - Optional user country code to include as a query parameter
 * @param params.locale - Optional locale; when provided, also sets `lang` and `forceLanguageChange=1`
 * @param params.currency - Optional currency code; when provided, also sets `forceCurrencyChange=1`
 * @param params.extraParams - Optional additional query parameters, applied after (and able to override) the standard ones
 * @returns The booking management URL, or '' when `bookingId` is missing or the host cannot be resolved
 */
export const getBookingManagementUrl = ({
  brand,
  appEnv,
  bookingId,
  anonymousId,
  userCountry,
  locale,
  currency,
  extraParams
}: {
  brand: Brand
  appEnv: string
  anonymousId?: string
  userCountry?: string
  locale?: string
  currency?: string
  bookingId: string
  extraParams?: Record<string, string>
}): string => {
  if (!bookingId) return ''
  try {
    const host = getSearchHost({brand, appEnv})
    if (!host) return ''
    const url = new URL(`/bookings/${bookingId}`, host)
    applyBookingsSearchParams(url, {anonymousId, userCountry, locale, currency})
    if (extraParams) {
      for (const [key, value] of Object.entries(extraParams)) {
        url.searchParams.set(key, value)
      }
    }
    return url.toString()
  } catch {
    return ''
  }
}

/**
 * Gets the sub-brand configuration for a given brand and sub-brand key.
 * @param brand - The brand object containing sub-brand configuration
 * @param subBrand - The sub-brand code identifier
 * @returns The sub-brand configuration object if found, otherwise undefined
 */
export const getSubBrandConfig = (
  brand: Brand,
  subBrand: string | undefined
): SubBrand | undefined => {
  if (!brand?.subBrand?.enabled || !subBrand) {
    return undefined
  }

  return brand.subBrand.brands[subBrand]
}

/**
 * Returns the help page URL in the appropriate localized language, if supported
 * @param brand - The brand object containing help links and locale info
 * @param lang - The language code (e.g. "en", "fr") to localize the URL
 * @returns The localized help URL, if available
 */
export const getLocalizedHelpUrl = (brand: Brand, lang = '') =>
  getLocalizedFooterUrl(brand.links.helpUrl, lang)

/**
 * Returns the contact page URL in the appropriate localized language, if supported
 * @param brand - The brand object containing contact links and locale info
 * @param lang - The language code (e.g. "en", "fr") to localize the URL
 * @returns The localized contact URL, if available
 */
export const getLocalizedContactUrl = (brand: Brand, lang = '') =>
  getLocalizedFooterUrl(brand.links.contactUrl, lang)

/**
 * Returns the disclosure page URL in the appropriate localized language, if supported
 * @param brand - The brand object containing disclosure links and locale info
 * @param lang - The language code (e.g. "en", "fr") to localize the URL
 * @returns The localized disclosure URL, if available
 */
export const getLocalizedDisclosureUrl = (brand: Brand, lang = '') =>
  getLocalizedFooterUrl(brand.links.disclosureUrl, lang)

/**
 * Returns a localized URL in case the URL is a Zendesk URL, otherwise returns the original URL.
 * In case the language contains a region code, only the country code is used.
 * @param url - The base URL to be localized
 * @param lang - The language code (with optional region, e.g. "en-US")
 * @returns The localized Zendesk URL
 */
export const getLocalizedFooterUrl = (url: string | undefined, lang = '') => {
  const [countryCode] = lang.split('-')
  return localizeZendeskLink(url, countryCode)
}

/**
 * Localize the given Zendesk URL
 * @param url - The URL to be potentially localized
 * @param lang - The language to localize (e.g. "en", "es")
 * @returns The localized or original URL
 */
const localizeZendeskLink = (url = '', lang = '') => {
  if (!url) return ''

  // English is the only language that uses a locale with a region code
  const finalLang = lang === 'en' ? 'en-us' : lang

  const {pathname = ''} = parseUrl(url)

  if (pathname == '/') {
    return url.endsWith('/')
      ? url.replace(/\/$/, `/hc/${finalLang}`)
      : `${url}/hc/${finalLang}`
  }

  if (pathname.includes('/hc/en-us'))
    return url.replace('/hc/en-us', `/hc/${finalLang}`)

  return url
}

// ==================== EMAIL TEMPLATE UTILITIES ====================

/**
 * Checks if a brand supports emails.
 * @param brand - The brand object to check
 * @returns True if the brand has email support enabled
 */
export const checkIsBrandEmailSupported = (brand: Brand): boolean =>
  brand.capabilities.tracking.emails?.enable === true

/**
 * Gets all brands that support emails.
 * @returns Array of brand codes that have email support enabled
 */
export const getSupportedEmailBrands = (): BrandCode[] =>
  Object.entries(brands)
    .filter(([, brandConfig]) => checkIsBrandEmailSupported(brandConfig))
    .map(([brandCode]) => brandCode as BrandCode)

/**
 * Whether the brand renders its own on-brand checkout payment page, either as
 * a Book-on-Brand merchant (e.g. Kiwi) or a co-branded flow (e.g. Finn).
 * @param brand - The brand object to check
 * @returns True if the brand has bookOnBrand or coBranded enabled
 */
export const checkBrandHasOwnCheckout = (brand: Brand): boolean =>
  brand.capabilities.commerce.bookOnBrand.enabled ||
  brand.capabilities.commerce.coBranded.enabled

/**
 * Gets all brands that render their own on-brand checkout. Derived from the
 * brand registry so new white-labels are picked up automatically.
 * @returns Array of brand codes with an on-brand checkout
 */
export const getBrandsWithOwnCheckout = (): BrandCode[] =>
  Object.entries(brands)
    .filter(([, brandConfig]) => checkBrandHasOwnCheckout(brandConfig))
    .map(([brandCode]) => brandCode as BrandCode)

/**
 * Gets the email workspace for a specific brand.
 * @param brandCode - The brand code to get workspace for
 * @returns The workspace name or undefined if not found
 */
export const getBrandEmailWorkspace = (
  brandCode: BrandCode
): string | undefined =>
  brands[brandCode]?.capabilities.tracking.emails?.cioWorkspace

/**
 * Get production workspace names from core brands config.
 * @returns Array of workspace names for brands with email support
 */
export const getProductionWorkspaceNames = (): string[] => {
  const workspaces = getSupportedEmailBrands()
    .map(getBrandEmailWorkspace)
    .filter((workspace): workspace is string => workspace !== undefined)

  return [...new Set(workspaces)].sort()
}

/**
 * Get the default locale for a specific workspace.
 * @param workspace - The workspace name to find the locale for
 * @returns The locale string for the workspace, or undefined if not found
 */
export const getLocaleForWorkspace = (
  workspace: string
): string | undefined => {
  const brandWithWorkspace = Object.values(brands).find(
    brand => brand.capabilities.tracking.emails?.cioWorkspace === workspace
  )
  return brandWithWorkspace?.capabilities.tracking.emails?.defaultLocale
}
